import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const MONTHS_EN = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];
const MONTHS_AR = [
  'يناير','فبراير','مارس','أبريل','مايو','يونيو',
  'يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'
];
const DAYS_EN = ['Su','Mo','Tu','We','Th','Fr','Sa'];
const DAYS_AR = ['أح','إث','ثل','أر','خم','جم','سب'];

const DatePicker = ({ value, onChange, minDate }) => {
  const { currentLang } = useLanguage();
  const isAr = currentLang === 'ar';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minD = minDate ? (() => { const d = new Date(minDate + 'T00:00:00'); d.setHours(0,0,0,0); return d; })() : today;

  const selected = value ? (() => { const d = new Date(value + 'T00:00:00'); return d; })() : null;

  const initYear = selected ? selected.getFullYear() : today.getFullYear();
  const initMonth = selected ? selected.getMonth() : today.getMonth();

  const [viewYear, setViewYear] = useState(initYear);
  const [viewMonth, setViewMonth] = useState(initMonth);

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const goBack = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const goForward = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const isDisabled = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    d.setHours(0, 0, 0, 0);
    return d < minD;
  };

  const isSelected = (day) => {
    if (!selected) return false;
    return (
      selected.getFullYear() === viewYear &&
      selected.getMonth() === viewMonth &&
      selected.getDate() === day
    );
  };

  const isToday = (day) => {
    const t = new Date();
    return t.getFullYear() === viewYear && t.getMonth() === viewMonth && t.getDate() === day;
  };

  const handleSelect = (day) => {
    if (isDisabled(day)) return;
    const d = new Date(viewYear, viewMonth, day);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    onChange(iso);
  };

  const MONTHS = isAr ? MONTHS_AR : MONTHS_EN;
  const DAYS = isAr ? DAYS_AR : DAYS_EN;

  const cells = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const formatSelected = () => {
    if (!selected) return null;
    if (isAr) return `${selected.getDate()} ${MONTHS[selected.getMonth()]} ${selected.getFullYear()}`;
    return `${MONTHS[selected.getMonth()]} ${selected.getDate()}, ${selected.getFullYear()}`;
  };

  return (
    <div className="bg-white border border-charcoal/15 rounded-sm shadow-sm overflow-hidden select-none">
      {/* Header */}
      <div className="flex items-center justify-between bg-charcoal/3 px-4 py-3 border-b border-charcoal/8">
        <button
          type="button"
          onClick={isAr ? goForward : goBack}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gold/15 text-charcoal/60 hover:text-gold transition-all"
        >
          {isAr ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
        <span className="font-semibold text-charcoal text-sm tracking-wide">
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          onClick={isAr ? goBack : goForward}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gold/15 text-charcoal/60 hover:text-gold transition-all"
        >
          {isAr ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>

      <div className="p-3">
        {/* Day labels */}
        <div className="grid grid-cols-7 mb-1">
          {DAYS.map((d) => (
            <div key={d} className="text-center text-xs font-semibold text-charcoal/35 py-1.5">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-0.5">
          {cells.map((day, i) =>
            day ? (
              <button
                key={i}
                type="button"
                onClick={() => handleSelect(day)}
                disabled={isDisabled(day)}
                className={`relative w-full aspect-square flex items-center justify-center text-sm font-medium rounded-full transition-all duration-150
                  ${isSelected(day)
                    ? 'bg-gold text-charcoal-deep shadow-md shadow-gold/30 scale-105'
                    : isToday(day)
                    ? 'text-gold border border-gold/40 hover:bg-gold/10'
                    : isDisabled(day)
                    ? 'text-charcoal/20 cursor-not-allowed'
                    : 'text-charcoal hover:bg-gold/10 hover:text-gold cursor-pointer'
                  }
                `}
              >
                {day}
              </button>
            ) : (
              <div key={i} />
            )
          )}
        </div>
      </div>

      {/* Selected display */}
      {selected && (
        <div className="px-4 py-2.5 bg-gold/5 border-t border-gold/15 text-center">
          <span className="text-sm font-semibold text-gold">{formatSelected()}</span>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
